// 組織樹畫面的純函式（SPEC #org-tree-ui）：動作寫成字、影響預覽的預設勾選與「仍有 N 人看得到」、樹的展開路徑。
import type { Assignment, MeTreeItem, Preview, PreviewPerson, RemainingAccess } from './orgTreeApi';
import { ROLE_ACTIONS, P6_EXPLICIT, type NodeRole } from './orgTreeApi';
import { dateStamp } from './timefmt';

type T = (k: string, o?: Record<string, unknown>) => string;

/** 動作 → 畫面上的短字（看 / 回放 / 事件 / 警報 / 對講 / 控制 / 布防 / 撤防 / 維運 / 匯出 / 家戶資料），固定順序、去重。 */
const WORD_ORDER: [string, string[]][] = [
  ['view', ['device.read', 'media.live']],
  ['playback', ['recording.read']],
  ['events', ['event.read']],
  ['alarm', ['alarm.receive']],
  ['talk', ['media.talk']],
  ['control', ['device.control']],
  ['arm', ['security.arm']],
  ['disarm', ['security.disarm']],
  ['maintain', ['device.maintain']],
  ['export', ['recording.export']],
  ['profile', ['home.profile.read']],
];

export function actionWordKeys(actions: string[]): string[] {
  const set = new Set(actions);
  return WORD_ORDER.filter(([, as]) => as.some((a) => set.has(a))).map(([w]) => w);
}

export function actionWords(t: T, actions: string[]): string {
  return actionWordKeys(actions)
    .map((w) => t('ot.act_' + w))
    .join(t('ot.dot'));
}

/** 指派的動作 = 角色動作 ∪ extra_actions；P6（受託方）不從角色帶入撤防 / 布防 / 維運 / 匯出。 */
export function roleActions(role: NodeRole, extra: string[] = [], customActions: string[] = [], p6 = false): string[] {
  const base = role === 'custom' ? customActions : ROLE_ACTIONS[role];
  const fromRole = p6 ? base.filter((a) => !P6_EXPLICIT.includes(a)) : base;
  return [...new Set([...fromRole, ...extra])];
}

/** 受託方：實際生效 = 指派動作 ∩ 委派範圍；不會生效的另列。 */
export function intersectScope(actions: string[], scopes: string[]): { effective: string[]; ignored: string[] } {
  const s = new Set(scopes);
  return { effective: actions.filter((a) => s.has(a)), ignored: actions.filter((a) => !s.has(a)) };
}

/** 預覽「仍有存取」預設勾哪些：只有 granted_by_losing 的列（選單 20）；擁有者這種保留列、不能撤的不勾。 */
export function defaultAlsoRevoke(rows: RemainingAccess[] | undefined): Set<string> {
  return new Set((rows ?? []).filter((r) => r.granted_by_losing && r.revocable !== false && !r.keep).map((r) => r.ref_id));
}

/**
 * 預覽回應補齊陣列欄位：撤銷 / 改節點指派的預覽（POST …/assignments/{id}/preview）只回 preview_token 與 granted_by_target，
 * 沒有 losing / gaining / delegations；對話框直接讀 .length 會拋錯、整個畫面變空白（BL-144，Bo 11-03 撤銷 rd7）。
 */
export function normPreview(p: Preview): Preview {
  return { ...p, losing: p.losing ?? [], gaining: p.gaining ?? [], delegations: p.delegations ?? [] };
}

/** 依目前勾選，寫入後仍看得到的人（名字 + 怎麼來的），主按鈕上方的黃字用（不重打預覽）。 */
export function stillVisible(rows: RemainingAccess[] | undefined, checked: Set<string>): { name: string; how: string }[] {
  const seen = new Map<string, string>();
  for (const r of rows ?? []) {
    if (checked.has(r.ref_id)) continue;
    if (!seen.has(r.display_name)) seen.set(r.display_name, r.detail ?? '');
  }
  return [...seen.entries()].map(([name, how]) => ({ name, how }));
}

/** 「會失去」的人數：成員群組展開；受託組織另計。 */
export function peopleCount(rows: PreviewPerson[] | undefined): { people: number; orgs: number } {
  let people = 0;
  let orgs = 0;
  for (const r of rows ?? []) {
    if (r.principal_kind === 'org') orgs++;
    else people += r.principal_kind === 'group' ? (r.member_count ?? 0) : 1;
  }
  return { people, orgs };
}

/** 409 preview_stale 重新預覽後，哪些列變了（新的有、舊的沒有）——畫面標出來，不清掉已選的。 */
export function changedRows(before: Preview | null, after: Preview): Set<string> {
  const key = (p: PreviewPerson) => `${p.principal_kind}:${p.principal_id ?? p.display_name}:${p.role ?? ''}`;
  const old = new Set([...(before?.gaining ?? []).map((p) => 'g:' + key(p)), ...(before?.losing ?? []).map((p) => 'l:' + key(p)), ...(before?.remaining_access ?? []).map((r) => 'r:' + r.ref_id)]);
  const out = new Set<string>();
  for (const p of after.gaining) if (!old.has('g:' + key(p))) out.add('g:' + key(p));
  for (const p of after.losing) if (!old.has('l:' + key(p))) out.add('l:' + key(p));
  for (const r of after.remaining_access ?? []) if (!old.has('r:' + r.ref_id)) out.add('r:' + r.ref_id);
  return out;
}
export function personKey(kind: 'g' | 'l', p: PreviewPerson): string {
  return `${kind}:${p.principal_kind}:${p.principal_id ?? p.display_name}:${p.role ?? ''}`;
}

/** 勾選裡留下仍出現在新預覽的（重新預覽不清掉已選的內容）。 */
export function keepChecked(checked: Set<string>, p: Preview): Set<string> {
  const ids = new Set([...(p.remaining_access ?? []).map((r) => r.ref_id), ...(p.granted_by_target ?? []).map((a) => a.assignment_id)]);
  return new Set([...checked].filter((x) => ids.has(x)));
}

/** 委派寫法：「<受託組織> › 委派：<委派方>」；窄螢幕在受託組織底下只寫「委派：<委派方>」。 */
export function delegationLabel(t: T, it: Pick<MeTreeItem, 'name' | 'delegation'>): string {
  const from = it.delegation?.from_org_name ?? it.name;
  return it.delegation?.whole_org ? t('ot.dlg_label_whole', { from }) : t('ot.dlg_label', { from });
}

/** 指派的期限 / 時段文字。 */
export function termText(t: T, a: Pick<Assignment, 'expires_at' | 'schedule'>, _lang: string): string {
  const parts: string[] = [];
  if (a.expires_at) parts.push(t('ot.until', { date: dateStamp(a.expires_at) }));
  for (const w of a.schedule?.windows ?? []) parts.push(t('ot.window', { from: w.from, to: w.to }));
  return parts.length ? parts.join(' · ') : t('ot.forever');
}

/** 從搜尋結果的 path（祖先 key）算出要展開的 key。 */
export function expandPathOf(it: Pick<MeTreeItem, 'path' | 'key'>): string[] {
  return (it.path ?? []).map((p) => p.key);
}

/** 即時頁樹頂層分組：我自己的（我是 owner 的組織、個人組織）→ 組織授權給我 → 別人分享給我。 */
export function topGroupOf(it: Pick<MeTreeItem, 'kind' | 'source' | 'org_kind' | 'id'>, ownedOrgIds: Set<string> = new Set()): 'own' | 'org' | 'shared' {
  if (it.kind === 'sharer') return 'shared';
  if (it.source) return it.source === 'shared' ? 'shared' : it.source;
  return it.org_kind === 'personal' || ownedOrgIds.has(it.id) ? 'own' : 'org';
}

/**
 * 組織與場域樹的數字（SPEC #org-tree-ui「設備數一律不含通道」，第二輪 R2-P1-11）：跟頁首、總覽同一種「台」——
 * 後端 subtree 分開回 devices / channels 時 devices 就是台數；舊後端只有一個合計（含通道），畫面寫明「含通道」，不跟頁首的台數混成同一種數。
 */
export function structCount(s: { devices: number; channels?: number } | undefined): { devices: number; channels?: number; inclChannels: boolean } | null {
  if (!s) return null;
  return { devices: s.devices, channels: s.channels, inclChannels: s.channels === undefined };
}

/** 節點詳細那一行：「底下 3 個節點、4 個戶、14 台設備、5 個通道（結構總數）」或舊後端「…、19 台（含通道）（結構總數）」。 */
export function structTotalsText(t: T, s: { nodes: number; homes: number; devices: number; channels?: number }): string {
  const c = structCount(s)!;
  const dev = c.inclChannels ? t('ot.struct_dev_incl', { count: c.devices }) : t('ot.struct_dev_split', { devices: c.devices, channels: c.channels ?? 0 });
  return t('ot.struct_totals2', { nodes: s.nodes, homes: s.homes, dev });
}
